import { isPlatformBrowser, CommonModule } from '@angular/common';
import * as i1$1 from '@angular/common/http';
import { HttpUrlEncodingCodec } from '@angular/common/http';
import * as i0 from '@angular/core';
import { Injectable, PLATFORM_ID, Inject, NgModule, Optional, inject, DestroyRef } from '@angular/core';
import * as i1$2 from 'angular-oauth2-oidc';
import { OAuthService, MemoryStorage, OAuthStorage, OAuthModuleConfig, OAuthModule } from 'angular-oauth2-oidc';
import * as i1 from 'ngx-cookie-service';
import { CookieService } from 'ngx-cookie-service';
import jwtDecode from 'jwt-decode';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { Router } from '@angular/router';

class SiAuthConfig {
}

/**
 * removes the scope parameter form the login url, so that we receive all scopes assigned to the user
 */
class OAuthDerivedService extends OAuthService {
    async createLoginUrl(state = '', loginHint = '', customRedirectUri = '', noPrompt = false, params = {}) {
        const superRes = await super.createLoginUrl(state, loginHint, customRedirectUri, noPrompt, params);
        let res;
        if (!superRes.includes('scope=')) {
            res = superRes;
        }
        else {
            const url = new URL(superRes);
            url.searchParams.delete('scope');
            res = url.href;
        }
        return res;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: OAuthDerivedService, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: OAuthDerivedService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: OAuthDerivedService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

const TENANT_ID_COOKIE_KEY = 'tenant_id';

/* eslint-disable-next-line arrow-body-style */
const createDefaultStorage = () => {
    return typeof sessionStorage !== 'undefined'
        ? sessionStorage
        : new MemoryStorage();
};
class SiOAuthStorageConfig {
}
const DOMAIN_EKS_DEV = 'siveillance-dev.siemens.cloud';
const DOMAIN_EKS_TEST = 'siveillance-test.siemens.cloud';
const DOMAIN_EKS_PROD = 'siveillance.siemens.com';
const SISHELL_COOKIE_NAME = 'sishellauthcookie';
const TENANT_ID_COOKIE_EXPIRATION_DAYS = 90;
class SiOAuthStorageService {
    constructor(cookieService, platformId) {
        this.cookieService = cookieService;
        this.storage = createDefaultStorage();
        this.cookiePath = '/';
        this.cookieName = SISHELL_COOKIE_NAME;
        this.isBrowser = isPlatformBrowser(platformId);
        if (this.isBrowser) {
            const myHref = location.href;
            if (myHref.includes(DOMAIN_EKS_DEV)) {
                this.cookieDomain = DOMAIN_EKS_DEV;
            }
            else if (myHref.includes(DOMAIN_EKS_TEST)) {
                this.cookieDomain = DOMAIN_EKS_TEST;
            }
            else if (myHref.includes(DOMAIN_EKS_PROD)) {
                this.cookieDomain = DOMAIN_EKS_PROD;
            }
        }
    }
    configure(config) {
        this.cookieDomain = config.authenticationCookieDomain ? config.authenticationCookieDomain : this.cookieDomain;
        this.cookiePath = config.authenticationCookiePath ? config.authenticationCookiePath : this.cookiePath;
        this.cookieName = config.authenticationCookieName ? config.authenticationCookieName : this.cookieName;
    }
    getItem(key) {
        if (this.useCookie(key)) {
            key = this.isAccessTokenKey(key) ? this.getAccessTokenKey() : key;
            if (!this.cookieService.check(key)) {
                return null;
            }
            return this.cookieService.get(key);
        }
        else {
            return this.storage.getItem(key);
        }
    }
    removeItem(key) {
        if (this.useCookie(key)) {
            key = this.isAccessTokenKey(key) ? this.getAccessTokenKey() : key;
            this.cookieService.delete(key, this.cookiePath, this.cookieDomain);
        }
        else {
            this.storage.removeItem(key);
        }
    }
    setItem(key, data) {
        if (this.useCookie(key)) {
            key = this.isAccessTokenKey(key) ? this.getAccessTokenKey() : key;
            // Since domain never changes, it could be a property/field of SiOAuthStorageService.
            // However, since this storage service is provided via a factory/provider pattern,
            // on ctor time SiAuthConfigService is not yet filled with values from config.
            this.cookieService.set(key, data, this.cookieExpiration(key), this.cookiePath, this.cookieDomain);
        }
        else {
            this.storage.setItem(key, data);
        }
    }
    isAccessTokenKey(key) {
        return key === 'access_token' || key === this.cookieName;
    }
    getAccessTokenKey() {
        return this.cookieName ?? 'access_token';
    }
    useCookie(key) {
        return this.isAccessTokenKey(key) || key === TENANT_ID_COOKIE_KEY;
    }
    cookieExpiration(key) {
        if (key === TENANT_ID_COOKIE_KEY) {
            return TENANT_ID_COOKIE_EXPIRATION_DAYS;
        }
        else {
            return undefined;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiOAuthStorageService, deps: [{ token: i1.CookieService }, { token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiOAuthStorageService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiOAuthStorageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.CookieService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }]; } });

/** Http client for the tenant resource */
class TenantService {
    constructor(httpClient) {
        this.httpClient = httpClient;
    }
    setTenantManagementUrl(tenantManagementUrl) {
        if (tenantManagementUrl) {
            this.apiUrl = this.getApiUrl(tenantManagementUrl);
        }
    }
    /** Get all tenants for the logged in user */
    async getMyTenants() {
        if (!this.apiUrl) {
            return [];
        }
        const url = this.apiUrl + '/me';
        const res = await this.httpClient.get(url).toPromise();
        return res.resourceList;
    }
    /** Returns url '<baseUrl>/api/tenants' */
    getApiUrl(baseUrl) {
        const separator = baseUrl.endsWith('/') ? '' : '/';
        return baseUrl + separator + 'api/tenants';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: TenantService, deps: [{ token: i1$1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: TenantService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: TenantService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1$1.HttpClient }]; } });

const safeJwtDecode = (token) => {
    if (!token) {
        return null;
    }
    try {
        return jwtDecode(token);
    }
    catch (_) {
        return null;
    }
};
class AuthService {
    set user(value) {
        this._user.next(value);
    }
    constructor(oauthService, oauthStorage, // DO use this to get, set and delete cookies.
    platformId, tenantService) {
        this.oauthService = oauthService;
        this.oauthStorage = oauthStorage;
        this.tenantService = tenantService;
        this._user = new BehaviorSubject(undefined);
        this.user$ = this._user.asObservable();
        this.isBrowser = isPlatformBrowser(platformId);
    }
    configure(siAuthConfig) {
        this.authConfig = siAuthConfig;
        this.oauthStorage.configure(siAuthConfig);
        this.oauthService.setStorage(this.oauthStorage);
        if (siAuthConfig.tenantManagementUrl) {
            this.tenantService.setTenantManagementUrl(siAuthConfig.tenantManagementUrl);
            this.authConfig.loginZone = siAuthConfig.loginZone ?? 'sisuite';
        }
        else {
            this.authConfig.loginZone = siAuthConfig.loginZone;
        }
        this.authConfig.issuer =
            siAuthConfig.issuer ??
                this.constructIssuer(this.authConfig.authServerUrl, this.authConfig.loginZone);
        this.authConfig.authorizationUrl =
            siAuthConfig.authorizationUrl ??
                this.constructAuthorizationUrl(this.authConfig.authServerUrl, this.authConfig.loginZone);
        if (!this.authConfig.authRedirectUrl) {
            this.authConfig.authRedirectUrl =
                window.location.origin + window.location.pathname;
            if (this.authConfig.authRedirectUrl.endsWith('/')) {
                this.authConfig.authRedirectUrl =
                    this.authConfig.authRedirectUrl.substring(0, this.authConfig.authRedirectUrl.length - 1);
            }
            this.authConfig.authRedirectUrl += window.location.hash;
        }
        if (this.isBrowser) {
            const constructedLogoutUrl = siAuthConfig.logoutUrl ??
                this.constructLogoutUrl(this.authConfig.authServerUrl, this.authConfig.loginZone);
            this.authConfig.logoutUrl =
                constructedLogoutUrl +
                    '?redirect=' +
                    encodeURIComponent(this.authConfig.authRedirectUrl) +
                    '&client_id=' +
                    this.authConfig.clientId;
        }
        this.authConfig.logoutButtonUrl =
            siAuthConfig.logoutButtonUrl ??
                this.constructLogoutUrl(this.authConfig.authServerUrl, this.authConfig.loginZone);
    }
    getClaims() {
        const claims = this.oauthService.getIdentityClaims();
        return claims ? claims : undefined;
    }
    hasAccessToken() {
        return this.oauthService.hasValidAccessToken();
    }
    getAccessToken() {
        return this.oauthService.getAccessToken() || undefined;
    }
    isCodeResponse() {
        const params = new URL(window.location.toString()).searchParams;
        return params.has('code') && params.has('state');
    }
    /**
     * Login tenant using auth code flow.
     *    If no tenant is given, it will be read from cookie.
     *    If no cookie is present, it will default to the first tenant.
     */
    async login(tenantId, identityProvider) {
        // The tenant to redirect to
        const token = this.oauthService.getAccessToken();
        const isTokenValid = this.oauthService.hasValidAccessToken();
        const cookieTenant = this.oauthStorage.getItem(TENANT_ID_COOKIE_KEY);
        let targetTenantId = tenantId ?? cookieTenant ?? undefined;
        let doPreLoginCleanup = !!(token && tenantId) || this.isCodeResponse();
        if (isTokenValid && token && !this.isCodeResponse() && !tenantId) {
            const parsedToken = safeJwtDecode(token);
            const storedAt = this.oauthStorage.getItem('access_token_stored_at');
            if (storedAt) {
                // we have stored meta info
                const claims = this.oauthService.getIdentityClaims();
                const tenantIdFromJwt = parsedToken.zid;
                if (!claims ||
                    tenantIdFromJwt !== cookieTenant ||
                    tenantIdFromJwt !== claims.zid) {
                    doPreLoginCleanup = true;
                    this.oauthStorage.setItem(TENANT_ID_COOKIE_KEY, tenantIdFromJwt);
                    targetTenantId = tenantIdFromJwt;
                }
                else if (!!tenantIdFromJwt &&
                    tenantIdFromJwt !== this.authConfig.loginZone) {
                    this.user = this.readUserFromClaims(claims);
                    this.oauthStorage.setItem(TENANT_ID_COOKIE_KEY, claims.zid);
                    return true;
                }
            }
            else {
                // got cookie from other scs? rerun login so we got all meta info like expires_at etc.
                this.oauthStorage.setItem(TENANT_ID_COOKIE_KEY, parsedToken.zid);
                targetTenantId = parsedToken.zid;
            }
        }
        // Configure the oauth service
        const partialConfig = this.setupAuthCodeFlowConfig(targetTenantId, identityProvider);
        const authConfig = this.getAuthConfig(partialConfig);
        this.oauthService.configure(authConfig);
        // Do the login
        if (doPreLoginCleanup) {
            this.preLoginCleanup();
        }
        const result = await this.oauthService.loadDiscoveryDocumentAndLogin();
        if (result) {
            const userInfo = (await this.oauthService.loadUserProfile());
            this.user = this.readUserFromClaims(userInfo);
            const issuerUrl = authConfig.issuer
                ? new URL(authConfig.issuer)
                : undefined;
            if (issuerUrl?.host.startsWith(this.authConfig.loginZone)) {
                // we are logged in to the sisuite zone -> get the first tenant and re-login to that tenant
                const tenants = await this.tenantService.getMyTenants();
                // redirecting to current tenant logout url and then logging into first tenant in list
                if (tenants.length === 0) {
                    // login completed, but got no tenants
                    return true;
                }
                const [firstTenant] = tenants;
                this.oauthStorage.setItem(TENANT_ID_COOKIE_KEY, firstTenant.id);
                if (this.authConfig.authServerUrl.startsWith('https')) {
                    this.doLogout(userInfo.zid, firstTenant.id);
                }
                else {
                    this.oauthService.logOut(true);
                    return this.login(firstTenant.id);
                }
                return false;
            }
            else {
                this.oauthStorage.removeItem('id_token');
                return true;
            }
        }
        else {
            return false;
        }
    }
    getSiAuthConfig() {
        return this.authConfig;
    }
    preLoginCleanup() {
        this.oauthStorage.removeItem('expires_at');
        this.oauthStorage.removeItem('id_token_claims_obj');
        this.oauthStorage.removeItem('id_token_expires_at');
        this.oauthStorage.removeItem('id_token_stored_at');
        this.oauthStorage.removeItem('access_token_stored_at');
        this.oauthStorage.removeItem('granted_scopes');
        this.oauthStorage.removeItem('session_state');
        this.oauthStorage.removeItem('access_token');
        this.oauthStorage.removeItem('id_token');
        this.oauthStorage.removeItem('refresh_token');
    }
    getGrantedScopes() {
        const scopesObj = this.oauthService.getGrantedScopes();
        if (!scopesObj) {
            return [];
        }
        if (Array.isArray(scopesObj) && scopesObj.length > 1) {
            return scopesObj;
        }
        if (scopesObj?.[0] && typeof scopesObj[0] === 'string') {
            return scopesObj[0].split(' ');
        }
        return [];
    }
    /**
     * Do a complete logout: logout from current tenant and sisuite zone.
     */
    logoutFromApp() {
        this.doLogout();
    }
    /**
     * Logout from the current tenant, if logged in at all, else do nothing.
     */
    logoutFromTenant() {
        const token = safeJwtDecode(this.oauthService.getAccessToken());
        if (token?.zid) {
            this.doLogout(token.zid);
        }
    }
    /**
     * Logout form the current tenant and login to new tenant @param newTenantId
     * @param newTenantId The id of the tenant to redirect to
     */
    async switchToTenant(newTenantId) {
        // uaa on non https does not support redirect whitelisting settings
        if (!this.authConfig.logoutUrl.startsWith('https')) {
            this.oauthService.logOut(true);
            this.oauthStorage.setItem(TENANT_ID_COOKIE_KEY, newTenantId);
            this.oauthStorage.setItem('id_token', 'not-used');
            await this.login(newTenantId);
            return;
        }
        const token = safeJwtDecode(this.oauthService.getAccessToken());
        this.oauthService.logOut(true);
        const logoutUrl = this.buildTenantSubdomain(this.authConfig.logoutUrl, token?.zid).toString();
        this.oauthStorage.setItem(TENANT_ID_COOKIE_KEY, newTenantId);
        this.oauthStorage.setItem('id_token', 'not-used');
        await this.executeLogoutInIFrame(logoutUrl);
    }
    /**
     * If no parameters are provided, the user will be logged out from all tenants + zones.
     * If a @param currentTenantId is provided, the user will be logged out from that tenant,
     * and then redirected to the app (thus he will be logged in to the default (first) tenant again).
     * If additionaly @param newTenantId is provided, the user will switch to that tenant,
     * i.e. log out from current tenant and log in to new tenant.
     * @param currentTenantId The id of the tenant to logout from
     * @param newTenantId The id of the tenant to redirect to
     */
    doLogout(currentTenantId, newTenantId) {
        // Create logout url
        let logoutUrl;
        if (!currentTenantId) {
            this.oauthStorage.removeItem(TENANT_ID_COOKIE_KEY);
            logoutUrl = this.authConfig.logoutButtonUrl;
        }
        else {
            logoutUrl = this.buildTenantSubdomain(this.authConfig.logoutUrl, currentTenantId).toString();
        }
        // Configure the oauth service
        const authConfig = this.getAuthConfig({ logoutUrl });
        this.oauthService.configure(authConfig);
        // we don't want that the oidc library adds an id_token_hint parameter
        this.oauthStorage.setItem('id_token', 'not-used');
        if (newTenantId) {
            // Overwrite tenant id cookie with the new tenant id or delete it
            this.oauthStorage.setItem(TENANT_ID_COOKIE_KEY, newTenantId);
        }
        this.oauthService.logOut();
    }
    executeLogoutInIFrame(logoutUrl) {
        return new Promise(resolve => {
            try {
                const iframe = document.createElement('iframe');
                iframe.src = logoutUrl;
                iframe.style.width = '0';
                iframe.style.height = '0';
                iframe.style.border = '0';
                iframe.style.display = 'none';
                const timer = setTimeout(() => {
                    // if after a second, the uaa logout page was not shown, proceed with normal logout anyway
                    this.redirect(logoutUrl);
                    resolve();
                }, 1000);
                iframe.addEventListener('load', () => {
                    clearTimeout(timer);
                    this.redirect(logoutUrl);
                    resolve();
                }, true);
                window.document.querySelectorAll('body')[0].appendChild(iframe);
            }
            catch (err) {
                // swallow error
                resolve();
            }
        });
    }
    redirect(url) {
        window.open(url, '_self', '');
    }
    /**
     * Takes a partial configuration object, fills up missing values with defaults from config and
     * returns a valid object for the configuration of the oauth service
     */
    getAuthConfig(partialAuthConfig) {
        const issuer = partialAuthConfig?.issuer ?? this.authConfig.issuer;
        const userinfoEndpoint = issuer.replace('oauth/token', '/userinfo');
        return {
            ...partialAuthConfig,
            // showDebugInformation: true
            clientId: this.authConfig.clientId,
            customQueryParams: partialAuthConfig?.customQueryParams ?? {},
            disableAtHashCheck: true,
            issuer,
            loginUrl: this.authConfig.authorizationUrl,
            logoutUrl: partialAuthConfig?.logoutUrl ?? this.authConfig.logoutUrl,
            oidc: true,
            redirectUri: this.authConfig.authRedirectUrl,
            requireHttps: issuer.startsWith('https'),
            responseType: 'code',
            scope: '',
            skipIssuerCheck: true,
            strictDiscoveryDocumentValidation: false,
            useIdTokenHintForSilentRefresh: false,
            userinfoEndpoint,
            waitForTokenInMsec: 1000
        };
    }
    readUserFromClaims(userInfo) {
        let name = userInfo?.given_name ? userInfo?.given_name + ' ' : '';
        name = name + (userInfo?.family_name ? userInfo?.family_name : '');
        name = name.trim();
        return userInfo
            ? {
                id: userInfo?.sub,
                username: userInfo?.email,
                email: userInfo?.email,
                name: name ? name : userInfo?.user_name,
                image: userInfo?.user_attributes?.picture || undefined
            }
            : undefined;
    }
    /** Prepare a partial auth config object, to be supplied to getAuthConfig() */
    setupAuthCodeFlowConfig(tenantId, identityProvider = 'sisuite-identities') {
        const config = {};
        if (tenantId && tenantId !== 'uaa') {
            config.issuer = this.buildTenantSubdomain(this.authConfig.issuer, tenantId).toString();
            config.customQueryParams = {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                client_secret: '',
                // eslint-disable-next-line @typescript-eslint/naming-convention
                login_hint: encodeURI(JSON.stringify({ origin: identityProvider }))
            };
        }
        else {
            config.issuer = this.authConfig.issuer;
            // eslint-disable-next-line @typescript-eslint/naming-convention
            config.customQueryParams = { client_secret: '' };
        }
        return config;
    }
    constructAuthorizationUrl(authServerUrl, zone) {
        const separator = authServerUrl.endsWith('/') ? '' : '/';
        if (zone) {
            const parts = authServerUrl.split('//');
            return (parts[0] + '//' + zone + '.' + parts[1] + separator + 'oauth/authorize');
        }
        return authServerUrl + separator + 'oauth/authorize';
    }
    constructIssuer(authServerUrl, zone) {
        const separator = authServerUrl.endsWith('/') ? '' : '/';
        if (zone) {
            const parts = authServerUrl.split('//');
            return (parts[0] + '//' + zone + '.' + parts[1] + separator + 'oauth/token');
        }
        return authServerUrl + separator + 'oauth/token';
    }
    constructLogoutUrl(authServerUrl, zone) {
        const separator = authServerUrl.endsWith('/') ? '' : '/';
        if (zone) {
            const parts = authServerUrl.split('//');
            return parts[0] + '//' + zone + '.' + parts[1] + separator + 'logout.do';
        }
        return authServerUrl + separator + 'logout.do';
    }
    buildTenantSubdomain(authUrl, subdomain) {
        const url = typeof authUrl === 'string' ? new URL(authUrl) : authUrl;
        const hostnameParts = url.hostname.split('.');
        if (subdomain) {
            hostnameParts[0] = subdomain;
        }
        url.hostname = hostnameParts.join('.');
        return url;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: AuthService, deps: [{ token: i1$2.OAuthService }, { token: SiOAuthStorageService }, { token: PLATFORM_ID }, { token: TenantService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: AuthService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: AuthService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$2.OAuthService }, { type: SiOAuthStorageService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }, { type: TenantService }]; } });

class OAuthModuleRuntimeConfig {
    constructor(authService) {
        this.authService = authService;
    }
    get resourceServer() {
        const config = this.authService.getSiAuthConfig() ?? {};
        return {
            customUrlValidation: config.customUrlValidation,
            allowedUrls: config.allowedUrls,
            sendAccessToken: Array.isArray(config.allowedUrls) || typeof config.customUrlValidation === 'function'
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: OAuthModuleRuntimeConfig, deps: [{ token: AuthService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: OAuthModuleRuntimeConfig, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: OAuthModuleRuntimeConfig, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: AuthService }]; } });

class SiAuthModule {
    static forRoot(configuration) {
        return {
            ngModule: SiAuthModule,
            providers: [
                {
                    provide: OAuthStorage,
                    useExisting: SiOAuthStorageService
                },
                {
                    provide: SiAuthConfig,
                    useValue: configuration
                },
                {
                    provide: OAuthModuleConfig,
                    useExisting: OAuthModuleRuntimeConfig
                },
                {
                    provide: OAuthService,
                    useClass: OAuthDerivedService
                },
                CookieService,
                HttpUrlEncodingCodec
            ]
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAuthModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.5", ngImport: i0, type: SiAuthModule, imports: [CommonModule, i1$2.OAuthModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAuthModule, imports: [CommonModule,
            OAuthModule.forRoot()] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAuthModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        OAuthModule.forRoot()
                    ]
                }]
        }] });

/**
 * Public interface for users of the si-auth module
 */
class SiOAuthService {
    constructor(authService, tenantService, siAuthConfig) {
        this.authService = authService;
        this.tenantService = tenantService;
        this.siAuthConfig = siAuthConfig;
        this.tenantsSub = new BehaviorSubject([]);
        this.tenants$ = this.tenantsSub.asObservable();
        this.selectedTenantSub = new BehaviorSubject(undefined);
        this.selectedTenant$ = this.selectedTenantSub.asObservable();
        this.isLoginCompletedSub = new BehaviorSubject(false);
        this.isLoginCompleted$ = this.isLoginCompletedSub.asObservable();
        this.user$ = this.authService.user$;
        this.isAuthenticatedSub = new BehaviorSubject(this.authService.hasAccessToken());
        this.isAuthenticated$ = this.isAuthenticatedSub.asObservable();
        this.claimsSub = new BehaviorSubject(this.authService.getClaims());
        this.claims$ = this.claimsSub.asObservable();
        this.accessTokenSub = new BehaviorSubject(this.authService.getAccessToken());
        this.accessToken$ = this.accessTokenSub.asObservable();
        let scopes = this.authService.getGrantedScopes();
        if (scopes.length === 0) {
            scopes = this.parseScopes(this.authService.getAccessToken());
        }
        this.scopesSub = new BehaviorSubject(scopes);
        this.scopes$ = this.scopesSub.asObservable();
    }
    // the following getters are convenience methods, since after login they do not change
    // and therefore can be retrieved without the use of observables
    getScopes() {
        return this.scopesSub.value;
    }
    isAuthenticated() {
        return this.isAuthenticatedSub.value;
    }
    /**
     * Indicates if login flow is completed.
     *
     * @returns True, if a successful login flow is completed.
     */
    isLoginCompleted() {
        return this.isLoginCompletedSub.value;
    }
    getAccessToken() {
        return this.accessTokenSub.value;
    }
    getTenants() {
        return this.tenantsSub.value;
    }
    getCurrentTenant() {
        return this.selectedTenantSub.value;
    }
    getClaims() {
        return this.claimsSub.value;
    }
    /**
     * Main entrance point to the SiOAuthModule. Call on every startup.
     */
    async login(tenantId, identityProvider) {
        try {
            if (!this.siAuthConfig) {
                throw new Error('No auth config found. Pass it to siOAuthService.configure(...) or provide the SiAuthConfig class in your module!');
            }
            if (this.siAuthConfig.shouldSkipAuthFlow) {
                this.isAuthenticatedSub.next(true);
                this.isLoginCompletedSub.next(true);
                return true;
            }
            this.authService.configure(this.siAuthConfig);
            const loginSucceedAndCompleted = await this.authService.login(tenantId, identityProvider);
            if (!loginSucceedAndCompleted) {
                // Login is called on every startup, even if a valid token is available.
                // Login is a 2-way process. After first redirect it is not done but
                // we can still have a valid token. As such we set the next state depending
                // on the current state.
                this.isAuthenticatedSub.next(this.isAuthenticated());
                return false;
            }
            else {
                this.accessTokenSub.next(this.authService.getAccessToken());
                this.claimsSub.next(this.authService.getClaims());
                this.scopesSub.next(this.authService.getGrantedScopes());
                const tenants = await this.tenantService.getMyTenants();
                this.tenantsSub.next(tenants);
                const claims = this.getClaims();
                const selectedId = claims?.zid ?? '';
                const selectedTenant = tenants.find(t => t.id === selectedId);
                this.selectedTenantSub.next(selectedTenant);
                this.isAuthenticatedSub.next(true);
                this.isLoginCompletedSub.next(true);
                return true;
            }
        }
        catch (err) {
            console.error('login failed due to error: ', err);
            return false;
        }
    }
    configure(siAuthConfig) {
        this.siAuthConfig = siAuthConfig;
        this.authService.configure(siAuthConfig);
    }
    switchToTenant(tenantId) {
        this.authService.switchToTenant(tenantId);
    }
    logout() {
        // kill session
        this.claimsSub.next(undefined);
        this.accessTokenSub.next(undefined);
        this.isAuthenticatedSub.next(false);
        this.tenantsSub.next([]);
        this.selectedTenantSub.next(undefined);
        this.scopesSub.next([]);
        // logout
        this.authService.logoutFromApp();
    }
    parseScopes(token) {
        if (!token) {
            return [];
        }
        try {
            const decodedToken = jwtDecode(token);
            return decodedToken.scope ? decodedToken.scope : [];
        }
        catch (_) {
            return [];
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiOAuthService, deps: [{ token: AuthService }, { token: TenantService }, { token: SiAuthConfig, optional: true }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiOAuthService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiOAuthService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: AuthService }, { type: TenantService }, { type: SiAuthConfig, decorators: [{
                    type: Optional
                }] }]; } });

// IIFE to capture the path because the path gets rewritten when authService.login() is called
const initialPath = (() => {
    let pathFromHash;
    if (typeof window !== 'undefined') {
        pathFromHash = window.location.hash.replace('#/', '');
    }
    const initialPathToNavigate = pathFromHash ? pathFromHash : '/';
    return () => initialPathToNavigate;
})();
const doFirefoxSpecialFeatureFix = () => {
    // The history API is buggy on firefox...
    const href = location.href
        .replace(/[&?]code=[^&$]*/, '')
        .replace(/[&?]state=[^&$]*/, '');
    window.location.replace(href);
};
const isFirefox = () => navigator.userAgent.toLowerCase().includes('firefox');
const isCloudHost = () => {
    const host = window.location.host;
    return ((host.includes('.siemens.') &&
        (host.includes('.building360') || host.includes('securitymanager'))) ||
        host === 'localhost:4201');
};
const siAuthGuard = () => {
    const authService = inject(SiOAuthService);
    const router = inject(Router);
    const platformId = inject(PLATFORM_ID);
    const isBrowser = isPlatformBrowser(platformId);
    let subscription;
    inject(DestroyRef).onDestroy(() => {
        subscription?.unsubscribe();
    });
    // check if the app is running behind an auth gateway.
    // This is duplicated code from SiAuthGatewayService.isBehindGateway()
    if (isBrowser && isCloudHost()) {
        return true;
    }
    const isAuthenticated = authService.isAuthenticated() && authService.isLoginCompleted();
    if (!isAuthenticated) {
        subscription = combineLatest([
            authService.isAuthenticated$,
            authService.isLoginCompleted$
        ]).subscribe(([authSuccessful, loginCompleted]) => {
            if (authSuccessful && loginCompleted) {
                if (isFirefox()) {
                    doFirefoxSpecialFeatureFix();
                }
                const path = initialPath();
                router.navigateByUrl(path);
            }
        });
    }
    return isAuthenticated;
};

class SimplSiShellAuthModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SimplSiShellAuthModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.5", ngImport: i0, type: SimplSiShellAuthModule, exports: [CommonModule,
            // SiMPL Modules
            SiAuthModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SimplSiShellAuthModule, imports: [CommonModule,
            // SiMPL Modules
            SiAuthModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SimplSiShellAuthModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [],
                    exports: [
                        CommonModule,
                        // SiMPL Modules
                        SiAuthModule
                    ]
                }]
        }] });

// Public API Surface

/**
 * Generated bundle index. Do not edit.
 */

export { DOMAIN_EKS_DEV, DOMAIN_EKS_PROD, DOMAIN_EKS_TEST, SISHELL_COOKIE_NAME, SiAuthConfig, SiAuthModule, SiOAuthService, SiOAuthStorageConfig, SiOAuthStorageService, SimplSiShellAuthModule, siAuthGuard };
